import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import TruckService from "../../services/Truck.service";
import TransporterService from "../../services/TransporterService";
import Modal from "../Modal/Modal";
import { transformTruckNumber } from "../../utils/general";
import CustomSelect from "../CoreComponents/CustomSelect";
import SearchableSelect from "../CoreComponents/SearchableSelect";
import { ModalButton, ModalFooter } from "../Modal/ModalLayout";

interface UpdateTruckModalProps {
  isOpen: boolean;
  onClose: () => void;
  truckData: {
    jobBreakupDetailId: number;
    currentTruckNumber: string;
    driverId: number;
    transporterId?: number;
    driverName?: string;
    transporterName?: string;
  } | null;
  onUpdateSuccess: () => void;
}

interface Driver {
  driver: {
    id: number;
    name: string;
  };
  transporter: {
    id: number;
    uniqueId: string;
    name: string;
  };
  currentTruck: {
    id: number;
    name: string;
  };
  available: boolean;
}

const UpdateTruckModal: React.FC<UpdateTruckModalProps> = ({
  isOpen,
  onClose,
  truckData,
  onUpdateSuccess,
}) => {
  const [newTruckNumber, setNewTruckNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const [selectedTruckOption, setSelectedTruckOption] = useState<
    "existing" | "new"
  >("existing");
  const [selectedExistingTruck, setSelectedExistingTruck] = useState("");
  const [trailerContainerType, setTrailerContainerType] = useState("");
  const [trailerCapacity, setTrailerCapacity] = useState("");
  const loader = useLoading();
  const [driversCache, setDriversCache] = useState<Driver[]>([]);
  const [isFetchingDrivers, setIsFetchingDrivers] = useState(false);
  const fetchInProgressRef = useRef(false);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  // Memoize the transformed current truck number to avoid unnecessary re-computations
  const transformedCurrentTruckNumber = useMemo(() => {
    return truckData?.currentTruckNumber
      ? transformTruckNumber(truckData.currentTruckNumber)
      : "N/A";
  }, [truckData?.currentTruckNumber]);

  // Memoize available truck numbers (excluding current one and only available drivers)
  const availableTruckNumbers = useMemo(() => {
    if (!driversCache || !truckData?.transporterId) return [];

    const currentTruckNumber = truckData.currentTruckNumber;
    const results: { value: string; label: string }[] = [];
    const seenTrucks = new Set<string>();

    // Use for loop for better performance with large datasets
    for (let i = 0; i < driversCache.length; i++) {
      const driver = driversCache[i];

      // Only include available drivers
      if (!driver.available) {
        continue;
      }

      const truckName = driver.currentTruck ? driver.currentTruck.name : null;

      // Skip if no truck or current truck and duplicates
      if (
        !truckName ||
        truckName === currentTruckNumber ||
        seenTrucks.has(truckName)
      ) {
        continue;
      }

      seenTrucks.add(truckName);
      results.push({
        value: truckName,
        label: transformTruckNumber(truckName),
      });
    }

    return results;
  }, [driversCache, truckData?.transporterId, truckData?.currentTruckNumber]);

  // Trailer container type options
  const trailerContainerTypeOptions = [
    { value: "GP", label: "GP" },
    { value: "HQ", label: "HQ" },
    { value: "OT", label: "OT" },
    { value: "FL", label: "FL" },
    { value: "RF", label: "RF" },
    { value: "HD", label: "HD" },
    { value: "DV", label: "DV" },
  ];

  // Trailer capacity options
  const trailerCapacityOptions = [
    { value: "DOUBLE_TWENTY", label: "Double Twenty" },
    { value: "SINGLE_TWENTY", label: "Single Twenty" },
    { value: "OPEN_TANK", label: "Open Tank" },
    { value: "FLAT_RACK", label: "Flat Rack" },
  ];

  const fetchDrivers = useCallback(async () => {
    if (!truckData?.transporterId) return;

    // Prevent multiple simultaneous calls
    if (fetchInProgressRef.current) {
      console.log("Driver fetch already in progress, skipping");
      return;
    }

    // Use cached data if available
    if (driversCache.length > 0) {
      console.log("Using cached drivers data");
      return;
    }

    try {
      fetchInProgressRef.current = true;
      setIsFetchingDrivers(true);
      const response = await TransporterService.getAllDrivers();

      if (!response?.data?.error && Array.isArray(response?.data?.data)) {
        setDriversCache(response.data.data);
      } else {
        console.error("Error fetching drivers:", response?.data?.message);
        setDriversCache([]);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setDriversCache([]);
    } finally {
      setIsFetchingDrivers(false);
      fetchInProgressRef.current = false;
    }
  }, [truckData?.transporterId, driversCache.length]);

  useEffect(() => {
    if (truckData && isOpen) {
      // Only reset form fields if this is a new driver (different driverId)
      // or if the modal is opening for the first time
      const shouldReset =
        !selectedDriverId || selectedDriverId !== truckData.driverId;

      console.log("Modal effect triggered:", {
        isOpen,
        driverId: truckData.driverId,
        selectedDriverId,
        shouldReset,
      });

      if (shouldReset) {
        console.log("Resetting form fields for new driver");
        setNewTruckNumber("");
        setError(null);
        setShowValidationError(false);
        setSelectedTruckOption("existing");
        setSelectedExistingTruck("");
        setTrailerContainerType("");
        setTrailerCapacity("");
        setSelectedDriverId(truckData.driverId);
      } else {
        console.log("Preserving form fields for existing driver");
      }

      fetchDrivers();
    }
  }, [isOpen, truckData?.driverId]); // Only depend on isOpen and driverId, not the entire truckData object

  // Reset selectedDriverId when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDriverId(null);
    }
  }, [isOpen]);

  const validateTruckNumber = (number: string) => {
    // Remove spaces and dashes for validation
    const cleanNumber = number.replace(/[-\s]/g, "");

    // Truck number format: 2 letters + 2 digits + 2 letters + 4 digits (e.g., MH46AF4375)
    const truckRegex = /^[A-Z]{2}\d{2}[A-Z]{2}\d{4}$/i;
    return truckRegex.test(cleanNumber);
  };

  const handleTruckNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setNewTruckNumber(value);

    // Clear validation error when user starts typing
    if (showValidationError) {
      setShowValidationError(false);
    }
  };

  const handleUpdateTruck = async () => {
    if (!truckData) return;

    // Reset error state
    setError(null);
    setShowValidationError(false);

    let finalTruckNumber = "";
    let finalTrailerContainerType = "";
    let finalTrailerCapacity = "";

    if (selectedTruckOption === "existing") {
      if (!selectedExistingTruck) {
        setShowValidationError(true);
        setError("Please select an existing truck number");
        return;
      }
      finalTruckNumber = selectedExistingTruck;
    } else {
      // New truck number
      const trimmedNumber = newTruckNumber.trim();
      if (!trimmedNumber) {
        setShowValidationError(true);
        setError("Truck number is required");
        return;
      }

      if (!validateTruckNumber(trimmedNumber)) {
        setShowValidationError(true);
        setError("Invalid truck number format (e.g., MH46AF4375)");
        return;
      }

      if (!trailerContainerType) {
        setShowValidationError(true);
        setError("Trailer container type is required for new trucks");
        return;
      }

      if (!trailerCapacity) {
        setShowValidationError(true);
        setError("Trailer capacity is required for new trucks");
        return;
      }

      finalTruckNumber = trimmedNumber;
      finalTrailerContainerType = trailerContainerType;
      finalTrailerCapacity = trailerCapacity;
    }

    try {
      loader.setLoading(true);

      const request: any = {
        driverId: truckData.driverId,
        oldTruckNumber: truckData.currentTruckNumber,
        newTruckNumber: finalTruckNumber,
      };

      // Add trailer details only for new trucks
      if (selectedTruckOption === "new") {
        request.trailerContainerType = finalTrailerContainerType;
        request.trailerCapacity = finalTrailerCapacity;
      }

      const response = await TruckService.updateDriverTruck(request);

      if (response.data?.error) {
        showToast(
          response.data.message || "Failed to update truck number",
          "error"
        );
      } else {
        showToast("Truck number updated successfully", "success");
        onUpdateSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error updating truck number:", error);
      showToast("Failed to update truck number", "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const isFormValid = () => {
    if (selectedTruckOption === "existing") {
      return !!selectedExistingTruck;
    } else {
      return (
        !!newTruckNumber.trim() && !!trailerContainerType && !!trailerCapacity
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Truck Number"
      className="component_modal_container update_truck_modal !max-w-[550px]"
      footer={
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleUpdateTruck}
            disabled={!isFormValid()}
          >
            Update Truck
          </ModalButton>
        </ModalFooter>
      }
    >
      <div className="space-y-6">
        {/* Current Truck Display */}
        <div className="bg-white rounded-lg p-5 border border-[#cbd2d9] shadow-sm">
          {/* <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-[#37372d] mb-2">
                Current Truck Number
              </h3>
              <p className="text-xl font-bold text-[#59585b]">
                {transformedCurrentTruckNumber}
              </p>
            </div>
          </div> */}

          {/* <h3 className="text-sm font-medium text-[#37372d] mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-[#1062b9]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Current Driver & Transporter Information
          </h3> */}
          <h3 className="text-sm font-medium text-[#37372d] mb-4">
            Driver Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Driver Name
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {truckData?.driverName || "N/A"}
              </p>
            </div>
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Current Truck Number
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {transformedCurrentTruckNumber || "N/A"}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Transporter Name
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {truckData?.transporterName || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Trip Information */}
        {/* <div className="bg-white rounded-lg p-5 border border-[#cbd2d9] shadow-sm">
          <h3 className="text-sm font-medium text-[#37372d] mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-[#1062b9]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Current Driver & Transporter Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">Driver</p>
              <p className="text-sm font-semibold text-[#59585b]">
                {truckData?.driverName || "N/A"}
              </p>
            </div>
            <div className="bg-[#f5f7f7] rounded-lg p-3">
              <p className="text-xs text-[#838285] mb-1 font-medium">
                Transporter
              </p>
              <p className="text-sm font-semibold text-[#59585b]">
                {truckData?.transporterName || "N/A"}
              </p>
            </div>
          </div>
        </div> */}

        {/* Selection Type */}
        <div className="bg-white rounded-lg p-5 border border-[#cbd2d9] shadow-sm">
          <h3 className="text-sm font-medium text-[#37372d] mb-4">
            Update Truck
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setSelectedTruckOption("existing")}
              className={`p-5 rounded-lg border-2 transition-all duration-200 ${
                selectedTruckOption === "existing"
                  ? "border-[#1062b9] bg-[#1062b9] bg-opacity-5 text-[#1062b9] shadow-sm"
                  : "border-[#cbd2d9] bg-white text-[#59585b] hover:border-[#1062b9] hover:border-opacity-50 hover:shadow-sm"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedTruckOption === "existing"
                      ? "border-[#1062b9] bg-[#1062b9]"
                      : "border-[#cbd2d9]"
                  }`}
                >
                  {selectedTruckOption === "existing" && (
                    <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                  )}
                </div>
                <div className="text-left">
                  <span className="font-semibold block">Select Existing</span>
                  <p className="text-xs text-[#838285] mt-1">
                    Choose from available trucks
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setSelectedTruckOption("new")}
              className={`p-5 rounded-lg border-2 transition-all duration-200 ${
                selectedTruckOption === "new"
                  ? "border-[#1062b9] bg-[#1062b9] bg-opacity-5 text-[#1062b9] shadow-sm"
                  : "border-[#cbd2d9] bg-white text-[#59585b] hover:border-[#1062b9] hover:border-opacity-50 hover:shadow-sm"
              }`}
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedTruckOption === "new"
                      ? "border-[#1062b9] bg-[#1062b9]"
                      : "border-[#cbd2d9]"
                  }`}
                >
                  {selectedTruckOption === "new" && (
                    <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                  )}
                </div>
                <div className="text-left">
                  <span className="font-semibold block">Add New</span>
                  <p className="text-xs text-[#838285] mt-1">
                    Enter new truck details
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="mt-6">
            {/* Existing Truck Selection */}
            {selectedTruckOption === "existing" && (
              <div className="space-y-4 w-full">
                <label className="required_field_label">
                  Select Available Truck
                </label>
                <div className="w-full">
                  <SearchableSelect
                    options={availableTruckNumbers}
                    onChange={(value) => setSelectedExistingTruck(value)}
                    placeholder={
                      isFetchingDrivers
                        ? "Loading available trucks..."
                        : "Choose an available truck number"
                    }
                    disabled={isFetchingDrivers}
                    loading={isFetchingDrivers}
                    searchPlaceholder="Search available truck numbers..."
                    className="w-full"
                  />
                </div>
                {availableTruckNumbers.length === 0 && !isFetchingDrivers && (
                  <p className="text-sm text-[#838285] italic">
                    No available trucks found for this transporter
                  </p>
                )}
              </div>
            )}

            {/* New Truck Input */}
            {selectedTruckOption === "new" && (
              <div className="space-y-5">
                <div className="form-group">
                  <div className="form-field">
                    <label className="required_field_label">
                      New Truck Number
                    </label>
                    <input
                      maxLength={11}
                      type="text"
                      value={newTruckNumber}
                      onChange={handleTruckNumberChange}
                      placeholder="Enter truck number (e.g., MH46AF4375)"
                      className={showValidationError ? "invalid_field" : ""}
                    />
                  </div>
                  <div className="form-help">
                    Format: 2 letters + 2 digits + 2 letters + 4 digits
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="w-full">
                    <label className="required_field_label">Trailer Type</label>
                    <div className="w-full">
                      <CustomSelect
                        options={trailerContainerTypeOptions}
                        onChange={(value) =>
                          setTrailerContainerType(
                            Array.isArray(value) ? value[0] : value
                          )
                        }
                        placeholder="Choose type"
                        label="Trailer Container Type"
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="w-full">
                    <label className="required_field_label">
                      Trailer Capacity
                    </label>
                    <div className="w-full">
                      <CustomSelect
                        options={trailerCapacityOptions}
                        onChange={(value) =>
                          setTrailerCapacity(
                            Array.isArray(value) ? value[0] : value
                          )
                        }
                        placeholder="Choose capacity"
                        label="Trailer Capacity"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Section */}

        {/* Error Message */}
        {showValidationError && error && (
          <div className="bg-[#ff0000] bg-opacity-5 border border-[#ff0000] border-opacity-30 rounded-lg p-4">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-[#ff0000] mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm text-[#ff0000] font-medium">{error}</p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UpdateTruckModal;
